import { useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Grid } from '@mui/material'

import AddressAutocompleteInput from '@/components/ui/inputs/AddressAutocompleteInput'
import InfoSection from '@/components/ui/sections/InfoSection'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { COUNTRY_OPTIONS, SAUDI_CITY_OPTIONS, withCurrentValueOption } from '@/constants/saudi-address'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { useIsSA } from '@/hooks/useIsSA'
import { ParsedGoogleAddress } from '@/utils/google-address'

import { PatientProfileFormData } from '../model/usePatientProfileForm'

export function AddressInfoSection({ editable }: { editable: boolean }) {
  useLingui()
  const isMobile = useIsMobile()
  // SEHA (en-SA): City and Country are drop-downs and there is no postal code.
  const isSA = useIsSA()
  const rhForm = useFormContext<PatientProfileFormData>()

  const city = useWatch({ control: rhForm.control, name: 'city' })
  const country = useWatch({ control: rhForm.control, name: 'country' })

  const cityOptions = useMemo(() => withCurrentValueOption(SAUDI_CITY_OPTIONS, city), [city])
  const countryOptions = useMemo(() => withCurrentValueOption(COUNTRY_OPTIONS, country), [country])

  const xs = isMobile ? 12 : 6

  const handleAddressSelected = useCallback(
    (address: ParsedGoogleAddress) => {
      const options = { shouldDirty: true, shouldValidate: true } as const
      // Never blank a field Google could not resolve — the user may have typed it already.
      if (address.street) rhForm.setValue('street', address.street, options)
      if (address.city) rhForm.setValue('city', address.city, options)
      if (address.country) rhForm.setValue('country', address.country, options)
      // SEHA has no postal code field, so there is nothing to fill there.
      if (!isSA && address.cp) rhForm.setValue('cp', address.cp, options)
    },
    [isSA, rhForm]
  )

  return (
    <InfoSection label={t`Address information`}>
      <Grid container spacing={3} sx={{ mt: '0px' }}>
        {editable && (
          <Grid item xs={12}>
            <AddressAutocompleteInput
              canonicalCountry={isSA}
              label={t`Search an address`}
              placeholder={t`Start typing an address to fill the fields below`}
              onSelect={handleAddressSelected}
            />
          </Grid>
        )}
        <Grid item xs={xs}>
          <TextInputController control={rhForm.control} id="street" fullWidth label={t`Street`} name="street" readOnly={!editable} />
        </Grid>
        <Grid item xs={xs}>
          {isSA ? (
            <SelectInputController control={rhForm.control} id="city" fullWidth label={t`City`} name="city" options={cityOptions} readOnly={!editable} />
          ) : (
            <TextInputController control={rhForm.control} id="city" fullWidth label={t`City`} name="city" readOnly={!editable} />
          )}
        </Grid>
        {!isSA && (
          <Grid item xs={xs}>
            <TextInputController control={rhForm.control} id="cp" fullWidth label={t`Postal code`} name="cp" readOnly={!editable} />
          </Grid>
        )}
        <Grid item xs={xs}>
          {isSA ? (
            <SelectInputController
              control={rhForm.control}
              id="country"
              fullWidth
              label={t`Country`}
              name="country"
              options={countryOptions}
              readOnly={!editable}
            />
          ) : (
            <TextInputController control={rhForm.control} id="country" fullWidth label={t`Country`} name="country" readOnly={!editable} />
          )}
        </Grid>
      </Grid>
    </InfoSection>
  )
}
